import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getEventBySlug } from '@/services/api';
import { Calendar, MapPin, Clock, ArrowLeft, Share2, Camera } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { motion } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import ImageModal from '@/components/ImageModal';
import SeoHead from '@/components/Seo';

interface Event {
    id: string;
    title: string;
    description: string;
    start_date: string;
    end_date: string;
    location: string;
    category?: string;
    photo: string;
    slug: string;
    ministry?: {
        id: number;
        name: string;
    };
    galleries?: {
        id: number;
        source: string;
        caption?: string;
        title: string;
    }[];
}

const EventDetails = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [event, setEvent] = useState<Event | null>(null);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);

    useEffect(() => {
        const fetchEvent = async () => {
            try {
                const res = await getEventBySlug(slug as string);
                setEvent(res.data);
            } catch (err) {
                console.error('Error fetching event:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchEvent();
    }, [slug]);

    const formatDateRange = (start: string, end: string) => {
        try {
            const startDate = parseISO(start);
            const endDate = parseISO(end);
            return startDate.toDateString() === endDate.toDateString()
                ? format(startDate, 'PPPP')
                : `${format(startDate, 'MMM do')} – ${format(endDate, 'MMM do, yyyy')}`;
        } catch (e) {
            return "Date TBD";
        }
    };

    const imageSrc = event?.photo?.startsWith('http')
        ? event.photo
        : `https://api.ibadansouthanglicandiocese.org.ng/${event?.photo}`;

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-[#fcfaf7]">
            <div className="flex flex-col items-center gap-3">
                <div className="h-10 w-10 border-4 border-amber-600/20 border-t-amber-600 rounded-full animate-spin" />
            </div>
        </div>
    );

    if (!event) return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-[#fcfaf7] px-4">
            <h2 className="text-3xl font-serif font-bold text-slate-800 mb-4">Event Not Found</h2>
            <Button onClick={() => navigate('/events')} variant="outline" className="rounded-full">
                Return to Calendar
            </Button>
        </div>
    );

    return (
        <div className="min-h-screen bg-[#fcfaf7]">
            {/* FIX: Added optional chaining and fallback for description */}
            <SeoHead 
                title={`${event.title} | Diocesan Event`} 
                description={event.description?.substring(0, 160) || "View details for this diocesan event."} 
            />

            {/* Hero Header */}
            <div className="relative h-[60vh] min-h-[400px] w-full overflow-hidden">
                <motion.img 
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 1.5 }}
                    src={imageSrc} 
                    alt={event.title}
                    className="w-full h-full object-cover brightness-50"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#fcfaf7] via-transparent to-transparent" />
                
                <div className="absolute inset-0 flex flex-col justify-end pb-12">
                    <div className="max-w-5xl mx-auto px-4 w-full">
                        <motion.div 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="space-y-4"
                        >
                            <Button 
                                onClick={() => navigate(-1)}
                                variant="ghost" 
                                className="text-white hover:bg-white/10 mb-4 -ml-4 rounded-full"
                            >
                                <ArrowLeft className="mr-2 h-4 w-4" /> Back to Events
                            </Button>
                            <Badge className="bg-amber-500 text-white border-none px-4 py-1">
                                {event.ministry?.name || 'Diocesan Event'}
                            </Badge>
                            <h1 className="text-4xl md:text-6xl font-serif font-bold text-slate-900 leading-tight">
                                {event.title}
                            </h1>
                        </motion.div>
                    </div>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-4 -mt-6 relative z-10 pb-20">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                    
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-8">
                        <div className="bg-white p-8 md:p-12 rounded-[2.5rem] shadow-sm border border-slate-100">
                            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-6">About the Event</h2>
                            <p className="text-slate-700 whitespace-pre-line leading-relaxed text-lg font-serif">
                                {event.description || "No description available for this event."}
                            </p>
                            
                            <div className="mt-12 flex items-center gap-4">
                                <Button variant="outline" className="rounded-full border-slate-200" onClick={() => window.print()}>
                                    Print Program
                                </Button>
                                <Button variant="ghost" className="rounded-full text-slate-500">
                                    <Share2 className="h-4 w-4 mr-2" /> Share
                                </Button>
                            </div>
                        </div>

                        {/* Gallery Section */}
                        {event.galleries && event.galleries.length > 0 && (
                            <div className="pt-8">
                                <div className="flex items-center gap-4 mb-8">
                                    <Camera className="h-5 w-5 text-amber-600" />
                                    <h2 className="text-2xl font-serif font-bold text-slate-800">Event Gallery</h2>
                                </div>
                                <div className="columns-2 md:columns-3 gap-4 space-y-4">
                                    {event.galleries.map((img) => (
                                        <motion.div
                                            key={img.id}
                                            whileHover={{ y: -5 }}
                                            className="break-inside-avoid overflow-hidden rounded-3xl shadow-sm cursor-pointer border border-slate-100"
                                            onClick={() => setSelectedImage(img.source)}
                                        >
                                            <img
                                                src={img.source}
                                                alt={img.caption || img.title}
                                                className="w-full h-auto object-cover hover:brightness-90 transition-all"
                                            />
                                            {img.caption && (
                                                <div className="p-4 text-xs italic text-slate-500 bg-white">
                                                    {img.caption}
                                                </div>
                                            )}
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sidebar Metadata */}
                    <div className="space-y-6">
                        <div className="bg-slate-900 text-white p-8 rounded-[2.5rem] sticky top-24 shadow-xl">
                            <h3 className="text-amber-500 text-xs font-bold uppercase tracking-widest mb-8">Logistics</h3>
                            
                            <div className="space-y-8">
                                <div className="flex gap-4">
                                    <div className="h-10 w-10 shrink-0 rounded-full bg-white/5 flex items-center justify-center">
                                        <Calendar className="h-5 w-5 text-amber-500" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-400 font-bold uppercase tracking-tighter mb-1">Date</p>
                                        <p className="text-sm font-serif">{formatDateRange(event.start_date, event.end_date)}</p>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="h-10 w-10 shrink-0 rounded-full bg-white/5 flex items-center justify-center">
                                        <Clock className="h-5 w-5 text-amber-500" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-400 font-bold uppercase tracking-tighter mb-1">Time</p>
                                        <p className="text-sm font-serif">{event.start_date ? format(parseISO(event.start_date), 'p') : 'TBA'} Prompt</p>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="h-10 w-10 shrink-0 rounded-full bg-white/5 flex items-center justify-center">
                                        <MapPin className="h-5 w-5 text-amber-500" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-400 font-bold uppercase tracking-tighter mb-1">Venue</p>
                                        <p className="text-sm font-serif leading-snug">{event.location}</p>
                                    </div>
                                </div>
                            </div>

                            {/* <Button className="w-full mt-12 bg-amber-600 hover:bg-amber-700 text-white rounded-2xl py-6 h-auto font-bold tracking-tight">
                                Add to Calendar
                            </Button> */}
                        </div>
                    </div>
                </div>
            </div>

            {/* Image Modal */}
            {selectedImage && <ImageModal imageUrl={selectedImage} onClose={() => setSelectedImage(null)} />}
        </div>
    );
};

export default EventDetails;